// src/offerPricing.ts
import currency from "currency.js";
var MONTHLY_NIGHTS = 28;
var MONTHLY_CANCELLATION_CODE = "LTSTAY";
function isMonthlyOffer(offer) {
  return offer?.cancellationFee?.code === MONTHLY_CANCELLATION_CODE;
}
function formatDisplayDate(date) {
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}
function formatISODateTime(date) {
  return date.toISOString();
}
function toDateTime(date) {
  return new Date(date);
}
function subtractDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
}
function isPrepaidOffer(offer) {
  return offer.prePaymentAmount && offer.prePaymentAmount.amount > 0;
}
function isServiceBasedTax(serviceName) {
  if (!serviceName) {
    return false;
  }
  const words = serviceName.split(" ");
  for (const word of words) {
    if (word.toLowerCase() === "tax") {
      return true;
    }
  }
  return false;
}
function getItemizedMonthlyRoomCharges(timeSlices) {
  const nights = timeSlices.length;
  const roomCharge = timeSlices.reduce(
    (acc, timeSlice) => {
      return currency(acc).add(timeSlice?.baseAmount?.grossAmount).value;
    },
    0
  );
  const formattedAvgRoomCharge = currency(roomCharge, {
    separator: ",",
    symbol: "$"
  }).divide(nights).format();
  return [
    {
      name: `Room Charges - ${formattedAvgRoomCharge} x ${nights} nights`,
      amount: roomCharge
    }
  ];
}
function getItemizedDailyRoomCharges(timeSlices) {
  return timeSlices.map((timeSlice) => {
    const formattedDate = formatDisplayDate(timeSlice.from);
    return {
      name: `Room Charge - ${formattedDate}`,
      amount: timeSlice?.baseAmount?.grossAmount
    };
  });
}
function getItemizedIncludedServices(timeSlices, services) {
  const nights = timeSlices.length;
  const nightlyIncludedServiceMap = timeSlices.reduce(
    (acc, timeSlice) => {
      if (timeSlice.includedServices && timeSlice.includedServices.length > 0) {
        timeSlice.includedServices.forEach((includedService) => {
          const amount = includedService.amount.grossAmount;
          const formattedAmount = currency(amount, {
            separator: ",",
            symbol: "$"
          }).format();
          const name = `${includedService.service?.name} - ${formattedAmount} x ${nights} nights`;
          if (!acc[name]) {
            acc[name] = amount;
          } else {
            const existingAmount = acc[name];
            acc[name] = currency(existingAmount).add(amount).value;
          }
        });
      }
      return acc;
    },
    {}
  );
  const nightlyIncludedServices = Object.keys(nightlyIncludedServiceMap).map(
    (serviceName) => {
      return {
        name: serviceName,
        amount: nightlyIncludedServiceMap[serviceName]
      };
    }
  );
  const includedServices = services.filter((service) => !isServiceBasedTax(service?.service?.name)).reduce((acc, service) => {
    const isNightlyService = service.dates && service.dates.length > 0;
    if (isNightlyService) {
      const amount = service.totalAmount.grossAmount;
      const nights2 = service.dates.length;
      const formattedAmount = currency(amount, {
        separator: ",",
        symbol: "$"
      }).divide(nights2).format();
      acc.push({
        name: `${service.service.name} - ${formattedAmount} x ${nights2} nights`,
        amount: service.totalAmount.grossAmount
      });
    } else {
      acc.push({
        name: service.service.name,
        amount: service.totalAmount.grossAmount
      });
    }
    return acc;
  }, []);
  return [...nightlyIncludedServices, ...includedServices];
}
function getItemizedServiceBasedTaxes(services) {
  return services.filter((service) => isServiceBasedTax(service?.service?.name)).reduce((acc, service) => {
    acc.push({
      name: service.service.name,
      amount: service.totalAmount.grossAmount
    });
    return acc;
  }, []);
}
function getItemizedFees(fees) {
  return fees.map((fee) => {
    return {
      name: fee.name,
      amount: fee.totalAmount.grossAmount
    };
  });
}
function getItemizedTaxes(taxDetails) {
  return taxDetails.reduce((acc, taxDetail) => {
    if (taxDetail.tax.amount > 0) {
      acc.push({
        name: "VAT",
        amount: taxDetail.tax.amount
      });
    }
    return acc;
  }, []) || [];
}
function getItemizedCityTaxes(cityTaxes) {
  return cityTaxes.reduce((acc, cityTax) => {
    if (cityTax.totalGrossAmount.amount > 0) {
      acc.push({
        name: cityTax.name,
        amount: cityTax.totalGrossAmount.amount
      });
    }
    return acc;
  }, []) || [];
}
function getItemizedAdditionalServices(additionalServices) {
  return additionalServices.map((additionalService) => {
    return {
      name: additionalService.service.name,
      amount: additionalService.totalAmount.grossAmount
    };
  });
}
function getItemizedAdditionalServiceFees(additionalServices) {
  return additionalServices.reduce((acc, additionalService) => {
    const { service, fees } = additionalService;
    const charges = fees?.map((fee) => {
      return {
        name: `${service.name} - ${fee.name}`,
        amount: fee.totalAmount.grossAmount
      };
    });
    return charges ? [...acc, ...charges] : acc;
  }, []);
}
function chunk(array, size) {
  return array.reduce((chunks, item, index) => {
    const chunkIndex = Math.floor(index / size);
    if (!chunks[chunkIndex]) {
      chunks[chunkIndex] = [];
    }
    chunks[chunkIndex].push(item);
    return chunks;
  }, []);
}
function calcTransientInstallments(offer, total) {
  const isPrepaid = isPrepaidOffer(offer);
  const transientInstallment = isPrepaid ? {
    type: "TransientPrepayment",
    amount: total
  } : {
    type: "TransientPayAtStay",
    amount: total
  };
  return [transientInstallment];
}
function getStayDates(timeSlices) {
  return timeSlices.map((timeSlice) => {
    const [date] = timeSlice.from.split("T");
    return date;
  });
}
function getFeesByDate(offer) {
  const { fees, timeSlices } = offer;
  const nightCount = timeSlices.length;
  if (!fees || fees.length === 0) {
    return {};
  }
  const distributedFees = fees.map((fee) => {
    const name = fee.name;
    const nightlyAmounts = currency(fee.totalAmount.grossAmount).distribute(
      nightCount
    );
    return {
      name,
      nightlyAmounts
    };
  });
  return timeSlices.reduce(
    (acc, timeSlice, index) => {
      const [date] = timeSlice.from.split("T");
      acc[date] = distributedFees.map(({ name, nightlyAmounts }) => {
        return {
          name,
          amount: nightlyAmounts[index].value
        };
      });
      return acc;
    },
    {}
  );
}
function getServicesByDate(offer, isTaxes) {
  const { services, timeSlices } = offer;
  if (!services || services.length === 0) {
    return {};
  }
  const nightCount = timeSlices.length;
  const stayDates = getStayDates(timeSlices);
  const filteredServices = services.filter((service) => {
    return isTaxes ? isServiceBasedTax(service?.service?.name) : !isServiceBasedTax(service?.service?.name);
  });
  if (!filteredServices || filteredServices.length === 0) {
    return {};
  }
  return filteredServices.reduce(
    (acc, service) => {
      const name = service.service.name;
      if (service.dates?.length) {
        service.dates.forEach(({ serviceDate, amount }) => {
          if (!acc[serviceDate]) {
            acc[serviceDate] = [];
          }
          acc[serviceDate].push({ name, amount: amount.grossAmount });
        });
      } else {
        const nightlyServices = currency(
          service.totalAmount.grossAmount
        ).distribute(nightCount);
        stayDates.forEach((date, index) => {
          if (!acc[date]) {
            acc[date] = [];
          }
          acc[date].push({ name, amount: nightlyServices[index].value });
        });
      }
      return acc;
    },
    {}
  );
}
function getCityTaxesByDate(offer) {
  const { cityTaxes, timeSlices } = offer;
  if (!cityTaxes || cityTaxes.length === 0) {
    return {};
  }
  const nightCount = timeSlices.length;
  const stayDates = getStayDates(timeSlices);
  return cityTaxes.reduce(
    (acc, cityTax) => {
      const name = cityTax.name;
      if (cityTax.dates?.length) {
        cityTax.dates.forEach(({ serviceDate, amount }) => {
          if (!acc[serviceDate]) {
            acc[serviceDate] = [];
          }
          acc[serviceDate].push({ name, amount: amount.grossAmount });
        });
      } else {
        const nightlyCityTaxes = currency(
          cityTax.totalGrossAmount.amount
        ).distribute(nightCount);
        stayDates.forEach((date, index) => {
          if (!acc[date]) {
            acc[date] = [];
          }
          acc[date].push({ name, amount: nightlyCityTaxes[index].value });
        });
      }
      return acc;
    },
    {}
  );
}
function calcMonthlyInstallments(offer) {
  const feesByDate = getFeesByDate(offer);
  const taxesByDate = getServicesByDate(offer, true);
  const cityTaxesByDate = getCityTaxesByDate(offer);
  const servicesByDate = getServicesByDate(offer, false);
  const timeSliceChunks = chunk(
    offer.timeSlices,
    MONTHLY_NIGHTS
  );
  const paymentInstallments = timeSliceChunks.map(
    (timeSlices, index) => {
      const isPrepayment = index === 0;
      const lastIndex = timeSlices.length - 1;
      const startDate = toDateTime(timeSlices[0].from);
      const endDate = toDateTime(timeSlices[lastIndex].to);
      const dueDate = isPrepayment ? startDate : subtractDays(startDate, 7);
      let installmentCharges = [];
      const installmentAmount = timeSlices.reduce(
        (acc, timeSlice) => {
          const [date] = timeSlice.from.split("T");
          const roomCharge = timeSlice.baseAmount?.grossAmount;
          const includedServices = timeSlice.includedServices?.map((service) => {
            return {
              name: service.service.name,
              amount: service.amount.grossAmount
            };
          }) || [];
          const fees = feesByDate[date] || [];
          const taxes = taxesByDate[date] || [];
          const cityTaxes = cityTaxesByDate[date] || [];
          const services = servicesByDate[date] || [];
          installmentCharges.push({
            date,
            roomCharge,
            includedServices,
            fees,
            taxes,
            cityTaxes,
            services
          });
          const feesAmount = fees.reduce((acc2, fee) => {
            return currency(acc2).add(fee.amount).value;
          }, 0);
          const taxesAmount = taxes.reduce((acc2, tax) => {
            return currency(acc2).add(tax.amount).value;
          }, 0);
          const cityTaxesAmount = cityTaxes.reduce((acc2, cityTax) => {
            return currency(acc2).add(cityTax.amount).value;
          }, 0);
          const includedServicesAmount = includedServices.reduce(
            (acc2, service) => {
              return currency(acc2).add(service.amount).value;
            },
            0
          );
          const servicesAmount = services.reduce((acc2, service) => {
            return currency(acc2).add(service.amount).value;
          }, 0);
          const nightlyAmount = currency(roomCharge).add(feesAmount).add(taxesAmount).add(cityTaxesAmount).add(includedServicesAmount).add(servicesAmount).value;
          return currency(acc).add(nightlyAmount).value;
        },
        0
      );
      const paymentInstallment = {
        type: isPrepayment ? "MonthlyPrepayment" : "MonthlyInstallment",
        amount: installmentAmount,
        startDate: formatISODateTime(startDate),
        endDate: formatISODateTime(endDate),
        dueDate: formatISODateTime(dueDate),
        nights: timeSlices.length
      };
      return paymentInstallment;
    }
  );
  return paymentInstallments;
}
function calcOfferPriceSummary(offer, additionalServices = []) {
  const {
    timeSlices = [],
    services = [],
    fees = [],
    taxDetails = [],
    cityTaxes = []
  } = offer;
  const isMonthly = isMonthlyOffer(offer);
  const itemizedRoomCharges = isMonthly ? getItemizedMonthlyRoomCharges(timeSlices) : getItemizedDailyRoomCharges(timeSlices);
  const itemizedIncludedServices = getItemizedIncludedServices(
    timeSlices,
    services
  );
  const itemizedServiceBasedTaxes = getItemizedServiceBasedTaxes(services);
  const itemizedFees = getItemizedFees(fees);
  const itemizedTaxes = getItemizedTaxes(taxDetails);
  const itemizedCityTaxes = getItemizedCityTaxes(cityTaxes);
  const itemizedAdditionalServices = getItemizedAdditionalServices(
    additionalServices
  );
  const itemizedAdditionalServiceFees = getItemizedAdditionalServiceFees(
    additionalServices
  );
  const itemizedTaxesAndFees = [
    ...itemizedAdditionalServiceFees,
    ...itemizedFees,
    ...itemizedServiceBasedTaxes,
    ...itemizedTaxes,
    ...itemizedCityTaxes
  ];
  const totalRoomCharges = itemizedRoomCharges.reduce(
    (acc, item) => currency(acc).add(item.amount).value,
    0
  );
  const totalIncludedServices = itemizedIncludedServices.reduce(
    (acc, item) => currency(acc).add(item.amount).value,
    0
  );
  const totalAdditionalServices = itemizedAdditionalServices.reduce(
    (acc, item) => currency(acc).add(item.amount).value,
    0
  );
  const totalTaxesAndFees = itemizedTaxesAndFees.reduce(
    (acc, item) => currency(acc).add(item.amount).value,
    0
  );
  const total = currency(totalRoomCharges).add(totalIncludedServices).add(totalAdditionalServices).add(totalTaxesAndFees).value;
  const installments = isMonthly ? calcMonthlyInstallments(offer) : calcTransientInstallments(offer, total);
  const totalPrepayment = isMonthly ? installments[0].amount : isPrepaidOffer(offer) ? total : 0;
  return {
    itemizedRoomCharges,
    itemizedIncludedServices,
    itemizedAdditionalServices,
    itemizedTaxesAndFees,
    totalRoomCharges,
    totalIncludedServices,
    totalAdditionalServices,
    totalTaxesAndFees,
    totalPrepayment,
    total,
    installments
  };
}
function updateOfferPricing(offer, additionalServices) {
  const pricingSummary = calcOfferPriceSummary(offer, additionalServices);
  return {
    ...offer,
    pricingSummary,
    prePaymentAmount: {
      ...offer.prePaymentAmount,
      amount: pricingSummary.totalPrepayment
    },
    totalGrossAmount: {
      ...offer.totalGrossAmount,
      amount: pricingSummary.total
    }
  };
}
function calcFixedNightlyRate(timeSlices) {
  const roomChargeSum = timeSlices.reduce(
    (acc, timeSlice) => {
      return currency(acc).add(timeSlice.baseAmount.grossAmount).value;
    },
    0
  );
  const nights = timeSlices.length;
  return currency(roomChargeSum).divide(nights).value;
}
function calcFixedNightlyRateOverride(timeSlices) {
  const roomChargeSum = timeSlices.reduce(
    (acc, timeSlice) => {
      return currency(acc).add(timeSlice.totalGrossAmount.amount).value;
    },
    0
  );
  const nights = timeSlices.length;
  return currency(roomChargeSum).divide(nights).value;
}
function calcMonthlyTimeSlices(timeSlices) {
  const fixedNightlyRate = calcFixedNightlyRate(timeSlices);
  return timeSlices.map((timeSlice, index) => {
    const includedServices = timeSlice.includedServices || [];
    const totalIncludedServiceAmount = includedServices.reduce(
      (acc, includedService) => {
        return currency(acc).add(includedService.amount.grossAmount).value;
      },
      0
    );
    return {
      ...timeSlice,
      baseAmount: {
        ...timeSlice.baseAmount,
        grossAmount: fixedNightlyRate,
        netAmount: fixedNightlyRate
      },
      totalGrossAmount: {
        ...timeSlice.totalGrossAmount,
        amount: currency(fixedNightlyRate).add(totalIncludedServiceAmount).value
      }
    };
  });
}
function updateOfferMonthlyTimeSlices(offer) {
  const timeSlices = calcMonthlyTimeSlices(offer.timeSlices);
  return {
    ...offer,
    timeSlices
  };
}
export {
  calcFixedNightlyRate,
  calcFixedNightlyRateOverride,
  calcMonthlyTimeSlices,
  calcOfferPriceSummary,
  updateOfferMonthlyTimeSlices,
  updateOfferPricing
};
//# sourceMappingURL=index.mjs.map